import { DateTime } from "luxon";
import type { RefObject } from "react";
import { useSelector } from "react-redux";
import type { SubmitQuotationRequestRQ } from "../../../../../api/quotation/types";
import { useApoAuth } from "../../../../../hooks";
import { SubagentUserSelector } from "./SubagentUserSelector";

interface QuotationRequestFormProps {
  formRef: RefObject<HTMLFormElement>;
  isDarkMode: boolean;
  defaultQuotationTitle: string;
  selectedSubagent: string | null;
  userIsSubagent: boolean;
  sourceEntity: string;
  sessionId: string | null;
  sessionUsername: string;
  onSubmit: (payload: SubmitQuotationRequestRQ) => void;
}

export function QuotationRequestForm({
  formRef,
  isDarkMode,
  defaultQuotationTitle,
  selectedSubagent,
  userIsSubagent,
  sourceEntity,
  sessionId,
  sessionUsername,
  onSubmit,
}: QuotationRequestFormProps) {
  const { user } = useApoAuth();
  const currentUsername = user?.name || user?.email || "";

  // Get target_entity from website metadata (member entity)
  const targetEntityFromMetadata = useSelector(
    (state: { WebsiteMetadata: { metadata: { source_entity: string } } }) => {
      return state?.WebsiteMetadata?.metadata?.source_entity || "";
    },
  );

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const quotationTitle = formData.get("quotation-title") as string;
    const extraComments = formData.get("extra-comments") as string;

    // Determine source_entity
    // If user is a subagent, source_entity is the sourceEntity from props
    // If user is not a subagent, source_entity is selectedSubagent if it has a value, otherwise targetEntityFromMetadata
    const sourceEntityValue = userIsSubagent
      ? sourceEntity
      : selectedSubagent || targetEntityFromMetadata;

    // target_entity is the member entity
    // If user is a member, target_entity is their own entity (sourceEntity)
    // If user is a subagent, target_entity should be the member entity from website metadata
    const memberEntity = sourceEntity.includes("___member")
      ? sourceEntity
      : targetEntityFromMetadata;

    // Determine target_username
    // If user is a subagent, use their current username
    // Otherwise, if a user is selected, use their username from formData, otherwise use session username
    const selectedUsername = formData.get("selectedUsername") as string | null;
    const targetUsername = userIsSubagent ? currentUsername : selectedUsername || sessionUsername;

    const payload: SubmitQuotationRequestRQ = {
      session_id: "",
      chat_session_uid: sessionId || "",
      extra_comments: extraComments || "",
      quotation_title: quotationTitle || "",
      source_entity: sourceEntityValue,
      target_entity: memberEntity,
      target_username: targetUsername,
      due_date: DateTime.now().plus({ days: 2 }).toISODate() || "",
    };

    onSubmit(payload);
  };

  return (
    <form
      ref={formRef}
      className={[
        "rounded-lg",
        "shadow-md",
        "border",
        "p-4",
        isDarkMode ? "bg-gray-700 border-gray-600" : "bg-gray-50 border-gray-200",
      ].join(" ")}
      onSubmit={handleSubmit}
    >
      <div className="space-y-4">
        {selectedSubagent && <input type="hidden" name="sub-agent" value={selectedSubagent} />}
        {!userIsSubagent && (
          <SubagentUserSelector isDarkMode={isDarkMode} selectedSubagent={selectedSubagent} />
        )}
        <div>
          <label
            htmlFor="quotation-title"
            className={`block text-sm font-medium mb-1 ${isDarkMode ? "text-gray-200" : "text-gray-700"}`}
          >
            Quotation Title
          </label>
          <input
            type="text"
            id="quotation-title"
            name="quotation-title"
            defaultValue={defaultQuotationTitle}
            className={[
              "w-full",
              "px-3",
              "py-2",
              "rounded-md",
              "border",
              "focus:outline-none",
              "focus:ring-2",
              "focus:ring-blue-500",
              isDarkMode
                ? "bg-gray-800 border-gray-600 text-gray-100"
                : "bg-white border-gray-300 text-gray-900",
            ].join(" ")}
          />
        </div>
        <div>
          <label
            htmlFor="extra-comments"
            className={`block text-sm font-medium mb-1 ${isDarkMode ? "text-gray-200" : "text-gray-700"}`}
          >
            Extra Comments
          </label>
          <textarea
            id="extra-comments"
            name="extra-comments"
            rows={4}
            className={[
              "w-full",
              "px-3",
              "py-2",
              "rounded-md",
              "border",
              "focus:outline-none",
              "focus:ring-2",
              "focus:ring-blue-500",
              "resize-none",
              isDarkMode
                ? "bg-gray-800 border-gray-600 text-gray-100"
                : "bg-white border-gray-300 text-gray-900",
            ].join(" ")}
          />
        </div>
      </div>
    </form>
  );
}
